import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Plus, Trash2, Download, Image as ImageIcon, Play, Printer } from 'lucide-react';
import { DataStore, ProjectImage } from '@/lib/data-store';
import { ImageUploadForm } from '@/components/ImageUploadForm';
import { ProjectBreadcrumb } from '@/components/ProjectBreadcrumb';
import { useProject } from '@/context/ProjectContext';
import { generateImageCode, getNextImageNumber } from '@/lib/image-utils';
import { exportToExcel } from '@/lib/export-utils';
import { printWithLetterhead, generateTableHTML } from '@/lib/print-utils';

const GambarProjek = () => {
  const data = DataStore.getData();
  const [projectImages, setProjectImages] = useState<ProjectImage[]>(data.projectImages || []);
  const [projects] = useState(data.projects || []);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProjek, setFilterProjek] = useState('');
  const [selectedImage, setSelectedImage] = useState<ProjectImage | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const { selectedProject, setSelectedProject } = useProject();

  const handleAddNew = () => {
    setShowForm(true);
  };

  const handleSubmit = (data: ProjectImage) => {
    const newImages = [...projectImages, data];
    setProjectImages(newImages);
    DataStore.saveData({ ...DataStore.getData(), projectImages: newImages });
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    const filtered = projectImages.filter(img => img.id !== id);
    setProjectImages(filtered);
    DataStore.saveData({ ...DataStore.getData(), projectImages: filtered });
  };

  const handleDownload = (image: ProjectImage) => {
    const link = document.createElement('a');
    link.href = image.file;
    link.download = image.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredImages = useMemo(() => projectImages.filter(img => {
    const matchSearch = img.projek.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       img.keterangan.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       img.kodGambar.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = !filterProjek || img.projek === filterProjek;
    const matchProject = !selectedProject || img.projek === selectedProject;
    return matchSearch && matchFilter && matchProject;
  }), [projectImages, searchTerm, filterProjek, selectedProject]);

  const groupedByProject = useMemo(() => filteredImages.reduce((acc, img) => {
    if (!acc[img.projek]) {
      acc[img.projek] = [];
    }
    acc[img.projek].push(img);
    return acc;
  }, {} as Record<string, ProjectImage[]>), [filteredImages]);

  const nextImageCode = generateImageCode(getNextImageNumber(projectImages));

  return (
    <div className="space-y-6">
      {selectedProject && (
        <ProjectBreadcrumb projectName={selectedProject} currentPage="images" />
      )}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">9. Gambar Projek</h1>
          <p className="text-slate-600 mt-1">Galeri gambar dan video dokumentasi projek</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {projectImages.length > 0 && (
            <>
              <Button 
                onClick={() => {
                  const tableHTML = generateTableHTML(filteredImages, [
                    { label: 'Kod Gambar', key: 'kodGambar' },
                    { label: 'Projek', key: 'projek' },
                    { label: 'Tarikh', key: 'tarikh' },
                    { label: 'Keterangan', key: 'keterangan' },
                    { label: 'Jenis', key: 'fileType' }
                  ]);
                  printWithLetterhead('DAFTAR GAMBAR PROJEK', tableHTML, 'GALERI GAMBAR / IMAGE GALLERY');
                }} 
                className="bg-green-600 hover:bg-green-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Printer size={18} />
                <span className="font-semibold">Cetak</span>
              </Button>
              <Button 
                onClick={() => exportToExcel(filteredImages, 'Gambar Projek')} 
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-4 py-2 h-auto"
              >
                <Download size={18} />
                <span className="font-semibold">Excel</span>
              </Button>
            </>
          )}
          <Button className="bg-red-600 hover:bg-red-700 gap-2 px-4 py-2 h-auto" onClick={handleAddNew}>
            <Plus size={18} />
            <span className="font-semibold">Muat Naik Gambar</span>
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-2">
            <p className="text-sm text-slate-600">Jumlah Gambar/Video</p>
            <p className="text-3xl font-bold text-blue-600">{projectImages.length}</p>
            <p className="text-xs text-slate-500">{Object.keys(groupedByProject).length} projek</p>
          </div>
        </CardContent>
      </Card>

      {/* Search & Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <Input 
                placeholder="Cari projek, keterangan, atau kod..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-300"
              />
            </div>
            <select 
              value={filterProjek}
              onChange={(e) => setFilterProjek(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Semua Projek</option>
              {projects.map((p) => (
                <option key={p.id} value={p.namaProjek}>
                  {p.namaProjek}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Images Gallery by Project */}
      {filteredImages.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-slate-500">
              Tiada gambar/video. Sila muat naik gambar baru.
            </div>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByProject).map(([projek, images]) => (
          <Card key={projek}>
            <CardHeader className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedProject(projek)}>
              <CardTitle className="text-lg text-blue-600 hover:underline">{projek}</CardTitle>
              <CardDescription>Jumlah {images.length} gambar/video</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {images.map((image) => (
                  <div key={image.id} className="group relative bg-slate-100 rounded-lg overflow-hidden border border-slate-200 hover:border-red-500 transition-all">
                    {/* Thumbnail */}
                    {image.fileType === 'image' ? (
                      <img 
                        src={image.file} 
                        alt={image.keterangan}
                        className="w-full h-48 object-cover cursor-pointer group-hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setSelectedImage(image);
                          setShowPreview(true);
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-slate-200 flex items-center justify-center group-hover:opacity-80 transition-opacity cursor-pointer" onClick={() => {
                        setSelectedImage(image);
                        setShowPreview(true);
                      }}>
                        <Play className="text-slate-400" size={48} />
                      </div>
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-end p-3 opacity-0 group-hover:opacity-100">
                      <div className="w-full space-y-2">
                        <p className="text-white text-xs font-medium truncate">{image.kodGambar}</p>
                        <p className="text-white text-xs truncate">{image.tarikh}</p>
                      </div>
                    </div>

                    {/* Info Section */}
                    <div className="p-3 bg-white border-t border-slate-200">
                      <p className="text-xs text-slate-600 font-medium mb-1">Kod: {image.kodGambar}</p>
                      <p className="text-xs text-slate-600 line-clamp-2 mb-2">{image.keterangan}</p>
                      <p className="text-xs text-slate-500 mb-3">{image.tarikh}</p>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs flex-1"
                          onClick={() => {
                            setSelectedImage(image);
                            setShowPreview(true);
                          }}
                        >
                          <ImageIcon size={12} />
                          Lihat
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs flex-1"
                          onClick={() => handleDownload(image)}
                        >
                          <Download size={12} />
                          Muat Turun
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs flex-1 text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(image.id)}
                        >
                          <Trash2 size={12} />
                          Padam
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Image Upload Form Modal */}
      {showForm && (
        <ImageUploadForm
          kodGambar={nextImageCode}
          projects={projects}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Image Preview Modal */}
      {showPreview && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-4xl bg-white rounded-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-slate-200">
              <div>
                <p className="font-semibold text-slate-900">{selectedImage.kodGambar}</p>
                <p className="text-sm text-slate-600">{selectedImage.projek}</p>
              </div>
              <button 
                onClick={() => setShowPreview(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                <Plus size={24} style={{ transform: 'rotate(45deg)' }} />
              </button>
            </div>

            {/* Preview Content */}
            <div className="bg-black flex items-center justify-center min-h-96 max-h-96 overflow-hidden">
              {selectedImage.fileType === 'image' ? (
                <img 
                  src={selectedImage.file} 
                  alt={selectedImage.keterangan}
                  className="w-full h-full object-contain"
                />
              ) : (
                <video 
                  src={selectedImage.file}
                  controls
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Info */}
            <div className="p-4 space-y-2">
              <div>
                <p className="text-sm text-slate-600 font-medium">Keterangan</p>
                <p className="text-slate-900">{selectedImage.keterangan}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Tarikh</p>
                  <p className="text-slate-900">{selectedImage.tarikh}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium">Fail</p>
                  <p className="text-slate-900 text-sm truncate">{selectedImage.fileName}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleDownload(selectedImage)}
                >
                  <Download size={16} />
                  Muat Turun
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 text-red-600 hover:text-red-700"
                  onClick={() => {
                    handleDelete(selectedImage.id);
                    setShowPreview(false);
                  }}
                >
                  <Trash2 size={16} />
                  Padam
                </Button>
                <Button 
                  className="flex-1"
                  onClick={() => setShowPreview(false)}
                >
                  Tutup
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GambarProjek;